<script setup>
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
    rating: {
        type: Number,
        default: 0,
    },
    maxStars: {
        type: Number,
        default: 5,
    },
    editable: {
        type: Boolean,
        default: false,
    },
    ratingTextHigh: {
        type: String,
        default: "",
    },
    ratingTextLow: {
        type: String,
        default: "",
    },
});

const { t } = useI18n();

const emits = defineEmits(["update:rating"]);

const internalRating = ref(props.rating);

// Watch for external changes to the rating prop
watch(
    () => props.rating,
    newRating => {
        internalRating.value = newRating;
    }
);

function updateRating(newRating) {
    if (props.editable) {
        internalRating.value = newRating;
        emits("update:rating", newRating);
    }
}
</script>

<template lang="pug">
.qmodii-rating-scale-cont
    .qmodii-rating-scale
        button.qmodii-rating-star(
            v-for="index in props.maxStars",
            :key="index",
            type="button",
            :class="`qmodii-rating-star-${index} ${index <= internalRating ? 'qm_icon-star-sld' : 'qm_icon-star-thn'}`",
            :aria-label="`Rated ${index} out of ${props.maxStars}`",
            @click="props.editable ? updateRating(index) : null"
        )
    .qmodii-rating-scale-text
        span.qmodii-rating-text-low.qmodii-secondary-text.qmodii-font-small(
            v-if="props.ratingTextLow"
        ) {{ t(props.ratingTextLow) }}
        span.qmodii-rating-text-high.qmodii-secondary-text.qmodii-font-small(
            v-if="props.ratingTextHigh"
        ) {{ t(props.ratingTextHigh) }}
</template>

<style scoped lang="stylus">
.qmodii-rating-scale-cont
    display flex
    flex-direction column
    max-width 300px
    gap 0.5em

    .qmodii-rating-scale
        display inline-flex
        width 100%
        justify-content space-between
        flex-wrap wrap

        .qmodii-rating-star
            background none
            border none
            cursor pointer
            color var(--qmodii-secondary-text)
            padding 0
            transition color 0.3s ease
            font-size 3em
            &:first-of-type
                padding 0
            &:hover
            &:focus
                color var(--qmodii-primary)
            &:focus
            &.qm_icon-star-sld
                color var(--qmodii-primary)
            &:last-of-type
                margin-right 0

    .qmodii-rating-scale-text
        display flex
        justify-content space-between

        .qmodii-rating-text
            text-align center
            font-size 0.8em
</style>
